import { Product } from "@/hooks/useProduct";
import { useRegion } from "@/marketingUtils/useRegion";
import { POPUP_TIMESTAMP_KEY } from "@/pages/blogs/[slug]";
import { RazorpayHandlerResponse } from "@/types/paymentTypes/razorpay";
import mixpanel from "@/utils/mixpanel";
import tracker from "@/utils/tracker";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import { getCookie } from "cookies-next";
import { Asterisk, Mail, Phone, User } from "lucide-react";
import { useRouter } from "next/router";
import * as React from "react";
import { Fragment } from "react";
import { useForm } from "react-hook-form";
import JoinWhatsAppModal from "./JoinWhatsappModal";
import { Loader } from "./Loader";
import { trackPixelEvent } from "@/hooks/useAdditionalPixel";

interface MarketingNewPaidFormProps {
  isOpen?: boolean;
  closeModal?: () => void;
  price: PriceObj | undefined;
  masterclass: any;
  products?: Product[];
  fixedComment?: string;
  isFreeMc?: boolean;
  type?: "modal" | "normal";
  btnClass?: string;
  formHeading?: string;
  redirectUrl?: string;
  bucket?: string;
  abTestType?: string;
  // to be implemented
  IntermediateForm?: React.ReactNode;
  customCtaText?: string;
  showNameField?: boolean;
  showEmailField?: boolean;
  slug?: string;
  mrktToken?: string;
  outerContainerClass?: string;
  showNameLabel?: boolean;
  showEmailLabel?: boolean;
  showPhoneLabel?: boolean;
  excelExportFormBuilderData?: any;
  excelPlatform?: string;
  exportDataBeforePayment?: boolean;
  bottomInfoText?: string;
  bottomInfoTextClass?: string;
  outerDialogClass?: string;
  isPopUp?: boolean;
  customSource?: string;
  isSendToWebEngage?: boolean;
  additionalPixelEventToFiredOnLead?: {
    eventName: string;
    eventType?: "standard" | "custom";
  };
}

export default function MarketingNewPaidForm({
  isOpen,
  closeModal,
  price,
  masterclass,
  products,
  fixedComment,
  isFreeMc = false,
  type = "modal",
  btnClass,
  formHeading,
  redirectUrl,
  IntermediateForm,
  customCtaText,
  showNameField = true,
  showEmailField = true,
  bucket,
  abTestType,
  slug,
  isPopUp,
  mrktToken,
  outerContainerClass,
  showNameLabel = true,
  showEmailLabel = true,
  showPhoneLabel = true,
  excelExportFormBuilderData: exlData,
  excelPlatform,
  exportDataBeforePayment = false,
  bottomInfoText,
  bottomInfoTextClass,
  outerDialogClass,
  customSource,
  isSendToWebEngage,
  additionalPixelEventToFiredOnLead
}: MarketingNewPaidFormProps) {
  const [userData, setUserData] = React.useState<EnrollNowSchema | undefined>(
    undefined,
  );
  const [isExlFormOpen, setExlFormOpen] = React.useState(false);

  const router = useRouter();
  const source = customSource ?? router.query["source"];
  const comment = fixedComment ? fixedComment : router.query["comment"];
  const tkn = router.query.tkn;

  const activeSlot = masterclass?.slots?.find((slot: any) => {
    return slot.active;
  });
  const activeWAGroup = activeSlot?.whatsappGroupLink;
  const [isLoading, setIsLoading] = React.useState(false);

  const {
    handleSubmit,
    reset,
    register,
    formState: { errors },
    watch,
  } = useForm<EnrollNowSchema>({
    defaultValues: {
      name: userData?.name,
      email: userData?.email,
      phone: userData?.phone,
    },
  });
  const [isTracked, setIsTracked] = React.useState({
    name: false,
    email: false,
    phone: false,
    localStorageUser: false,
  });
  const name = watch("name");
  const email = watch("email");
  const phone = watch("phone");
  const region = useRegion();

  const submitLead = async (data: EnrollNowSchema) => {
    try {
      setIsLoading(true);
      mixpanel.alias(data.phone);
      mixpanel.people.set({
        $name: data.name,
        $email: data.email,
        $phone: data.phone,
      });
      mixpanel.identify(data.phone);

      setUserData(data);

      const fbclid = getCookie("_fbc") ?? router.query.fbclid ?? undefined;
      const fbpid = getCookie("_fbp") ?? undefined;
      const leadReffererMetadata = {
        ...(fbclid && { fbclid }),
        ...(fbpid && { fbpid }),
        ...(router.query.gclid && { gclid: router.query.gclid }),
      };

      const leadPayload = {
        name: !showNameField ? "astrolearn-user" : data.name,
        email: !showEmailField ? "user@astrolearn.in" : data.email,
        phone: data.phone,
        slotId: activeSlot?.id,
        source: source || "direct",
        origin: window.location.href,
        comment: comment || "default_coment",
        payload: {
          ...(bucket &&
            abTestType && {
            bucket,
            abTestType,
          }),
          isFreeMc,
        },
        region,
        reffererMetadata: leadReffererMetadata,
      };
      const { data: leadData } = await axios.post(
        `${process.env.NEXT_PUBLIC_LEADS_API}leads`,
        leadPayload,
      );
      try {
        if (isPopUp) {
          localStorage.setItem(POPUP_TIMESTAMP_KEY, new Date().toISOString());
        }
      } catch (error) {
        console.log(error);
      }
      mixpanel.track("lead_submit", {
        source: source || "direct",
        comment: comment || "default_comment",
        type: isFreeMc ? "free" : "paid",
        masterclassId: masterclass?.id,
        activeSlot: activeSlot?.id,
        abTestType: abTestType,
        bucket: bucket,
        activeWaGroup: activeSlot?.whatsappGroupLink,
        slug: slug,
        startDateTime: activeSlot?.startDateTime,
      });
      if (source == "twitter" && isFreeMc) {
        document.dispatchEvent(
          new CustomEvent("twitter_lead_submitted_free", {
            detail: {
              masterclassSlotId: activeSlot.id,
              masterclassId: masterclass.id,
            },
          }),
        );
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "twitter_lead_submitted_free" });
      }
      if (additionalPixelEventToFiredOnLead && slug) {
        const { eventName, eventType } = additionalPixelEventToFiredOnLead;
        trackPixelEvent(slug, eventName, eventType)
      }
      return leadData;
    } catch (error: any) {
      if (
        error?.response?.data?.message == "Lead already created for this slot"
      ) {
        mixpanel.track("retry_lead_submit", {
          source: source || "direct",
          comment: comment || "default_comment",
          type: isFreeMc ? "free" : "paid",
          masterclassId: masterclass?.id,
          activeSlot: activeSlot?.id,
          activeWaGroup: activeWAGroup,
          abTestType: abTestType,
          bucket: bucket,
          slug: slug,
          startDateTime: activeSlot?.startDateTime,
        });
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          window.location.href = `${window.location.origin}/${masterclass.id}/redirect/thank-you?source=${source}&comment=${comment}`;
        }
      }
      console.error(error);
    }
  };
  const [showJoiningPopup, setShowJoiningPopup] = React.useState(false);
  const submitForm = async (data: EnrollNowSchema) => {
    try {
      const leadData = await submitLead(data);
      tracker("trackSingleCustom", `enroll now button click`);
      tracker("trackSingleCustom", `Lead`, undefined, leadData?.id);
      tracker("trackSingleCustom", `LeadV2`, undefined, leadData?.id);
      if (exlData) {
        setIsLoading(false);
        setExlFormOpen(true);
      } else {
        if (!isFreeMc || products) {
          if (data.email != "haggachecker@gmail.com") {
            await paymentHandler(data);
          } else {
            setIsLoading(false);
            if (redirectUrl) {
              setIsLoading(false);
              window.location.href = redirectUrl;
            } else {
              setShowJoiningPopup(true);
            }
            return;
          }
        } else {
          if (closeModal) {
            setIsLoading(false);
            closeModal();
          }
          if (redirectUrl) {
            setIsLoading(false);
            window.location.href = redirectUrl;
          } else {
            setIsLoading(false);
            setShowJoiningPopup(true);
          }
        }
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    if (!isTracked["name"] && name?.length > 5) {
      tracker("trackSingleCustom", `ENP name Filled`);
      setIsTracked({ ...isTracked, name: true });
    }
    if (!isTracked["email"] && email?.length > 5) {
      tracker("trackSingleCustom", `ENP email Filled`);
      setIsTracked({ ...isTracked, email: true });
    }
    if (!isTracked["phone"] && phone?.length > 5) {
      tracker("trackSingleCustom", `ENP phone Filled`);
      setIsTracked({ ...isTracked, phone: true });
    }
  }, [name, email, phone]);
  React.useEffect(() => {
    if (userData) {
      localStorage.setItem("tfu-user-auth", JSON.stringify(userData));
    }
  }, [userData]);

  React.useEffect(() => {
    const localData = localStorage.getItem("tfu-user-auth");
    const localDataParsed = JSON.parse(localData ?? "{}");

    if (localData) {
      setUserData({
        name: localDataParsed.name,
        phone: localDataParsed.phone,
        email: localDataParsed.email,
      });
      reset({
        name: localDataParsed.name,
        phone: localDataParsed.phone,
        email: localDataParsed.email,
      });
      tracker("trackSingleCustom", `ENP Render Lead Data Prefilled`);
    } else {
      tracker("trackSingleCustom", `ENP Render Lead Data Empty`);
    }
  }, []);
  const comments = fixedComment ? fixedComment : router.query.comment;

  const overallFormdata = new FormData();
  const exportToExcel = async (data?: any) => {
    overallFormdata.append("platform", excelPlatform || "astrolearn-misc");
    overallFormdata.append("source", (source as string) || "direct");
    overallFormdata.append("name", userData?.name || "test-name");
    overallFormdata.append("email", userData?.email || "user@astrolearn.co");
    overallFormdata.append("phone", userData?.phone || "default");
    overallFormdata.append("comment", (comment as string) || "default");

    try {
      await axios.post(
        process.env.NEXT_PUBLIC_GOOGLE_SHEET_ASTROLEARN_LEAD!,
        overallFormdata,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    }
  };
  const paymentHandler = async (data: EnrollNowSchema) => {
    const userName = data?.name;
    const userPhone = data?.phone;
    const userEmail = data?.email || `user-${userPhone?.slice(-4)}@astrolearn.in`;
    const cartPayload = {
      masterclassId: masterclass?.id,
      products,
      priceSelector: (price as PriceObj).source,
      source: source,
      comments,
      user: {
        name: userData?.name ?? `user-${userPhone?.slice(-4)}`,
        email: userData?.email ?? `user-${userPhone?.slice(-4)}@astrolearn.in`,
        phone: userPhone,
      },
      ...(mrktToken !== undefined && { mrktToken }),
    };

    tracker("trackSingleCustom", `Mx Payment Init Mrkt`);
    try {
      const { data } = await axios.post(
        `${process.env.NEXT_PUBLIC_BASE_API}cart`,
        cartPayload,
      );

      const { gatewayOrderId, amount } = data.data;

      const options = {
        prefill: {
          name: userData?.name ?? `user-${userPhone?.slice(-4)}`,
          email: userData?.email ?? `user-${userPhone?.slice(-4)}@astrolearn.in`,
          contact: "+91" + String(userPhone),
        },
        key: process.env.NEXT_PUBLIC_RAZORPAY_KEY,
        amount: Number(amount),
        currency: "INR",
        name: `Masterclass: ${masterclass?.title}`,
        description: masterclass?.title,
        image: "/img/logo.png",
        handler: async function (data: RazorpayHandlerResponse) {
          tracker("trackSingleCustom", `Mx Payment Mrkt`);
          tracker(
            "trackSingleCustom",
            `Mx Payment Mrkt V2`,
            undefined,
            data?.razorpay_order_id,
          );

          if (isSendToWebEngage) {
            await axios.post(
              `${process.env.NEXT_PUBLIC_EVENT_LOGGER_URL}event/webengage/instant`,
              {
                eventName: "Ebook bundle purchase",
                appName: "ASTROLEARN",
                propagation: true,
                phone: userPhone,
                metaData: {
                  course_name: masterclass?.title,
                  price: Number(amount) || 99,
                  date: new Date(),
                  source: source,
                  comment: comment,
                  whatsapp_group_link: activeWAGroup,
                },
              },
            );
          }
          if (!exportDataBeforePayment && exlData) {
            await exportToExcel();
          }
          if (closeModal) {
            closeModal();
          }
          mixpanel.track("purchase", {
            page_referrer: document.referrer,
            platform: "astrolearn",
            amount: amount,
            masterClassId: masterclass.id,
            activeSlot: activeSlot?.id,
            activeWaGroup: activeWAGroup,
            slug: slug,
            abtesttype: abTestType,
            bucket: bucket,
            startDateTime: activeSlot?.startDateTime,
          });
          if (additionalPixelEventToFiredOnLead && slug && ["shhalu-ahuja",
            "vivek-khanna",
            "raunak-mishra"].includes(slug)) {
            trackPixelEvent(slug, "Mx Payment", "custom")
          }
          setTimeout(() => {
            if (redirectUrl) {
              window.location.href = redirectUrl;
            } else {
              window.location.href = `${window.location.origin}/${masterclass.id}/redirect/thank-you?source=${source}&comment=${comment}`;
            }
          }, 500);
        },
        modal: {
          ondismiss: () => {
            mixpanel.track("rzp_dismissed", {
              page_referrer: document.referrer,
              platform: "astrolearn",
              amount: amount,
              masterClassId: masterclass.id,
              activeSlot: activeSlot?.id,
              activeWaGroup: activeWAGroup,
              slug: slug,
              abtesttype: abTestType,
              bucket: bucket,
              startDateTime: activeSlot?.startDateTime,
            });
            window.location.href = `${window.location.origin}/${masterclass.id}/redirect/cancelled?source=${source}&comment=${comment}`;
          },
        },
        order_id: gatewayOrderId,
        theme: {
          color: "#000",
        },
      };

      // @ts-ignore
      const rzp1 = new window.Razorpay(options);
      rzp1.open();
    } catch (error) {
      console.error("[ERROR while create order intent OR Checkout]", error);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      <JoinWhatsAppModal
        isOpen={showJoiningPopup}
        masterclass={masterclass}
        onClose={() => { }}
        joiningLink={activeWAGroup}
        slug={slug || ""}
      />
      <ExcelFormModal
        isOpen={isExlFormOpen}
        onCloseModal={() => setExlFormOpen(false)}
        excelFormProps={exlData}
        btnClass={btnClass}
        userData={userData}
        submitHandler={(data: EnrollNowSchema) => {
          if (!isFreeMc || products) {
            setIsLoading(true);
            paymentHandler(data);
          } else {
            if (closeModal) {
              setIsLoading(false);
              closeModal();
            }
            if (redirectUrl) {
              setIsLoading(false);
              window.location.href = redirectUrl;
            } else {
              setIsLoading(false);
              setShowJoiningPopup(true);
            }
          }
        }}
        overallFormData={overallFormdata}
        exportDataBeforePayment={exportDataBeforePayment}
        exportToExcel={exportToExcel}
        outerDialogClass={outerDialogClass}
      />
      <Loader isActive={isLoading} />
      {type == "modal" && closeModal != undefined && isOpen != undefined ? (
        <MarketingNewModal
          isOpen={isOpen}
          onCloseModal={closeModal}
          formHeading={formHeading}
          outerDialogClass={outerDialogClass}
        >
          <form onSubmit={handleSubmit(submitForm)} className="space-y-4 !text-black">
            {showNameField && (
              <div
                onClick={() => {
                  mixpanel.track("marketing_form", {
                    input: "Name Edited",
                    type: "paid",
                  });
                }}
              >
                <Input
                  label="Name"
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  register={register}
                  errors={errors}
                  rules={{ required: showNameField }}
                  outerclassName="!w-full"
                />
              </div>
            )}
            {showEmailField && (
              <div
                onClick={() => {
                  mixpanel.track("marketing_form", {
                    input: "Email Edited",
                    type: "paid",
                  });
                }}
              >
                <Input
                  type="email"
                  name="email"
                  label="Email"
                  placeholder="Enter your email"
                  register={register}
                  errors={errors}
                  rules={{ required: showEmailField }}
                  outerclassName="!w-full"
                />
              </div>
            )}
            <div
              onClick={() => {
                mixpanel.track("marketing_form", {
                  input: "Phone Edited",
                  type: "paid",
                });
              }}
            >
              <Input
                type="tel"
                name="phone"
                label="phone"
                placeholder="Enter your phone number"
                register={register}
                errors={errors}
                rules={{
                  required: true,
                  maxLength: {
                    value: 10,
                    message: "Invalid Phone Number",
                  },
                  minLength: {
                    value: 10,
                    message: "Invalid Phone Number",
                  },
                  pattern: {
                    value: /^[6-9]\d{9}$/,
                    message:
                      "Please enter a valid 10-digit Indian mobile number (starting with 6, 7, 8, or 9)",
                  },
                }}
                outerclassName="!w-full"
              />
            </div>
            <div></div>
            <div className="mt-4 bottom-3 inset-x-3">
              <button
                type="submit"
                className={`border-none btn-block mt-3 btn-blue btn-lg !tracking-wider !text-2xl ${btnClass}`}
                disabled={isLoading}
              >
                {isLoading ? "Submitting" : (customCtaText ?? "Enroll Now")}
              </button>
              {bottomInfoText && (
                <div
                  className={`text-xs text-gray-600 italic flex items-end text-center place-self-center ${bottomInfoTextClass}`}
                >
                  <Asterisk className="text-red-500 w-3" />
                  {bottomInfoText}
                </div>
              )}
            </div>
          </form>
        </MarketingNewModal>
      ) : (
        <>
          {" "}
          <div
            className={`w-full flex flex-col gap-5 md:border-1  transform overflow-hidden md:rounded-2xl bg-white  text-left align-middle transition-all md:justify-center md:items-center border-5 p-6 ${outerContainerClass}`}
          >
            <div className="flex items-center justify-between">
              <h3
                className="text-lg font-medium leading-6 text-center"
                dangerouslySetInnerHTML={{
                  __html: formHeading ?? "Enroll Now",
                }}
              />
            </div>

            <form
              onSubmit={handleSubmit(submitForm)}
              className="space-y-4 !w-full"
            >
              {showNameField && (
                <div
                  onClick={() => {
                    mixpanel.track("marketing_form", {
                      input: "Name Edited",
                      type: "paid",
                    });
                  }}
                  className="w-full flex rounded-lg items-center relative"
                >
                  <Input
                    {...(showNameLabel && { label: "Name" })}
                    type="text"
                    name="name"
                    placeholder="Harshad Prasad"
                    register={register}
                    errors={errors}
                    rules={{ required: showNameField }}
                    className="!border-none  focus:outline-none focus:border-none focus:ring-0 !pr-8"
                    outerclassName="!w-full !border-none"
                    outerInputclassName=" border-2 rounded-lg"
                  />
                  <div className="absolute right-2 top-8">
                    <User className="text-[#000]" width={25} height={25} />
                  </div>
                </div>
              )}
              {showEmailField && (
                <div
                  onClick={() => {
                    mixpanel.track("marketing_form", {
                      input: "Email Edited",
                      type: "paid",
                    });
                  }}
                  className="w-full flex rounded-lg items-center relative"
                >
                  <Input
                    type="email"
                    name="email"
                    {...(showEmailLabel && { label: "Email" })}
                    placeholder="harshad@gmail.com"
                    register={register}
                    errors={errors}
                    rules={{ required: showEmailField }}
                    className="!border-none  focus:outline-none focus:border-none focus:ring-0 !pr-8"
                    outerclassName="!w-full !border-none"
                    outerInputclassName=" border-2 rounded-lg"
                  />
                  <div className="absolute right-2 top-8">
                    <Mail className="text-[#000]" width={25} height={25} />
                  </div>
                </div>
              )}
              <div
                onClick={() => {
                  mixpanel.track("marketing_form", {
                    input: "Phone Edited",
                    type: "paid",
                  });
                }}
                className="w-full flex rounded-lg items-center relative"
              >
                <Input
                  type="tel"
                  name="phone"
                  {...(showPhoneLabel && { label: "Phone" })}
                  placeholder="Enter your phone number"
                  register={register}
                  errors={errors}
                  rules={{
                    required: true,
                    maxLength: {
                      value: 10,
                      message: "Invalid Phone Number",
                    },
                    minLength: {
                      value: 10,
                      message: "Invalid Phone Number",
                    },
                    pattern: {
                      value: /^[6-9]\d{9}$/,
                      message:
                        "Please enter a valid 10-digit Indian mobile number (starting with 6, 7, 8, or 9)",
                    },
                  }}
                  className="!border-none  focus:outline-none focus:border-none focus:ring-0 !pr-8"
                  outerclassName="!w-full !border-none "
                  outerInputclassName=" border-2 rounded-lg"
                />
                <div className="absolute right-2 bottom-0 h-[42px] flex items-center">
                  <Phone className="text-[#000]" width={20} height={20} />
                </div>
              </div>
              <div></div>
              <div className="mt-4 bottom-3 inset-x-3">
                <button
                  type="submit"
                  className={`border-none btn-block mt-3 btn-blue btn-lg !tracking-wider !text-2xl ${btnClass}`}
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting" : (customCtaText ?? "Enroll Now")}
                </button>
                {bottomInfoText && (
                  <div
                    className={`text-xs text-gray-600 italic flex items-end text-center place-self-center ${bottomInfoTextClass}`}
                  >
                    <Asterisk className="text-red-500 w-3" />
                    {bottomInfoText}
                  </div>
                )}
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
}

export function Input({
  type,
  placeholder,
  name,
  register,
  errors,
  rules,
  label,
  className,
  disabled = false,
  outerclassName,
  outerInputclassName,
  handlePhoneChange,
  ...rest
}: {
  type: string;
  placeholder: string;
  name: string;
  register: any;
  errors: any;
  rules?: any;
  className?: string;
  label?: string;
  disabled?: boolean;
  outerclassName?: string;
  outerInputclassName?: string;
  handlePhoneChange?: any;
}) {
  return (
    <div className={`max-md:w-72 ${outerclassName}`}>
      {label && (
        <label>
          {label} {rules?.required && <span className="text-red-600">*</span>}
        </label>
      )}
      <div className={`${outerInputclassName} w-full h-fit !text-black`}>
        <input
          type={type}
          placeholder={placeholder}
          className={`${errors[name] && "input-invalid"} ${className}`}
          disabled={disabled}
          onChange={(e) => {
            handlePhoneChange(e.target.value);
          }}
          {...register(name, rules)}
          {...rest}
        />
      </div>
      {errors[name] && (
        <small className="mt-1.5 text-red-600 block">
          {errors[name].message || "This field is required"}
        </small>
      )}
    </div>
  );
}
interface ExcelFormProps {
  formMetadata?: any;
  platform?: string;
  ctaText?: string;
  btnClass?: string;
  submitHandler: (params?: any) => any;
  userData?: EnrollNowSchema;
  overallFormData: FormData;
  exportDataBeforePayment: boolean;
  exportToExcel?: (...args: any) => any;
}
interface ExcelFormModalProps {
  isOpen: boolean;
  onCloseModal: () => void;
  submitHandler: (params?: any) => any;
  excelFormProps: ExcelFormProps;
  btnClass?: string;
  formHeading?: string;
  userData?: EnrollNowSchema;
  exportDataBeforePayment?: boolean;
  overallFormData: FormData;
  exportToExcel?: (...args: any) => any;
  outerDialogClass?: string;
}

export function ExcelFormModal({
  isOpen,
  onCloseModal,
  submitHandler,
  excelFormProps,
  btnClass,
  formHeading,
  userData,
  exportDataBeforePayment = false,
  overallFormData,
  exportToExcel,
  outerDialogClass,
}: ExcelFormModalProps) {
  return (
    <MarketingNewModal
      isOpen={isOpen}
      onCloseModal={onCloseModal}
      formHeading={formHeading}
      outerDialogClass={outerDialogClass}
    >
      <ExcelForm
        {...excelFormProps}
        submitHandler={submitHandler}
        btnClass={btnClass}
        userData={userData}
        overallFormData={overallFormData}
        exportDataBeforePayment={exportDataBeforePayment}
        exportToExcel={exportToExcel}
      />
    </MarketingNewModal>
  );
}
const defaultFormMetaData = {
  fields: [
    {
      name: "dob",
      type: "date",
      label: "Date of Birth",
      placeholder: "Select your date of birth",
      rules: { required: true, message: "Date of Birth is required" },
      outerclassName: "!w-full",
    },
    {
      name: "tob",
      type: "time",
      label: "Time of Birth",
      placeholder: "Select your time of birth",
      rules: { required: true, message: "Time of Birth is required" },
      outerclassName: "!w-full",
    },
    {
      name: "pob",
      type: "text",
      label: "Place of Birth",
      placeholder: "Enter your place of birth",
      rules: {
        required: true,
        minLength: { value: 2, message: "Minimum 2 characters" },
      },
      outerclassName: "!w-full",
    },
    {
      name: "gender",
      type: "select",
      label: "Gender",
      placeholder: "Select gender",
      options: [
        { value: "", label: "Select gender" },
        { value: "male", label: "Male" },
        { value: "female", label: "Female" },
        { value: "other", label: "Other" },
      ],
      rules: { required: true, message: "Gender is required" },
      outerclassName: "!w-full",
    },
  ],
};

export const ExcelForm = ({
  formMetadata = defaultFormMetaData,
  platform,
  submitHandler,
  ctaText,
  btnClass,
  userData,
  exportToExcel,
  overallFormData,
  exportDataBeforePayment = false,
}: ExcelFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = (data: any) => {
    formMetadata.fields.forEach((field: any) => {
      overallFormData.append(field.name, data[field.name] || "");
    });
    if (exportDataBeforePayment) {
      exportToExcel?.(data);
    }
    submitHandler?.();
  };
  const renderField = (field: any) => {
    const { name, type, label, placeholder, rules, outerclassName, options } =
      field;

    if (type === "select") {
      return (
        <div key={name} className="mb-4">
          <div className={`max-md:w-72 ${outerclassName}`}>
            {label && (
              <label>
                {label}{" "}
                {rules?.required && <span className="text-red-600">*</span>}
              </label>
            )}
            <div className={` w-full h-fit`}>
              <select className={` `} {...register(name, rules)}>
                {options.map((option: any) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={name} className="mb-4">
        <Input
          label={label}
          type={type}
          name={name}
          placeholder={placeholder}
          register={register}
          errors={errors}
          rules={rules}
          outerclassName={outerclassName}
        />
      </div>
    );
  };

  return (
    <div className="">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {formMetadata.fields.map(renderField)}
        <button
          type="submit"
          className={`border-none btn-block mt-3 btn-blue btn-lg !tracking-wider !text-2xl ${btnClass}`}
        >
          {ctaText ?? "Confirm your Seat Now!"}
        </button>
      </form>
    </div>
  );
};

export const MarketingNewModal = ({
  isOpen,
  children,
  onCloseModal,
  formHeading,
  outerDialogClass,
}: {
  children: React.ReactNode;
  isOpen: boolean;
  onCloseModal: () => any;
  formHeading?: string;
  outerDialogClass?: string;
}) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={onCloseModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-70" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div
            className={`flex min-h-full md:items-center justify-center md:p-4 text-center ${outerDialogClass}`}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col gap-5 md:border-4 md:max-w-md transform overflow-hidden md:rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6"
                  >
                    {formHeading ?? "Enroll Now"}
                  </Dialog.Title>
                  <span>
                    <XMarkIcon
                      className="w-5 h-5 cursor-pointer"
                      onClick={onCloseModal}
                    />
                  </span>
                </div>
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
